import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { holidayAPI } from '../utils/api';
import type { CreateHolidayRequest, Holiday, UpdateHolidayRequest } from '../types/holiday';
import { formatDateOnly, getTodayDateInCompanyTimezone, toDateOnly } from '../utils/timezone';


const emptyCreateHoliday = (companyId: string): CreateHolidayRequest => ({
  name: '',
  date: '',
  is_recurring: false,
  company_id: companyId,
});

const getHolidayErrorMessage = (err: unknown, fallback: string) => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string; error?: string } } }).response;
    return response?.data?.message || response?.data?.error || fallback;
  }

  return fallback;
};

const Holidays: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'holidays' });
  const { t: tc } = useTranslation('common');
  const { currentCompany, companiesLoading, userRolePermissionsLoading } = useAuth();
  const { canView, canAdd, canEdit, canDelete } = useRolePermissions();

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deletingHoliday, setDeletingHoliday] = useState<Holiday | null>(null);
  const [newHoliday, setNewHoliday] = useState<CreateHolidayRequest>(emptyCreateHoliday(''));
  const [editHoliday, setEditHoliday] = useState<UpdateHolidayRequest>({
    name: '',
    date: '',
    is_recurring: false,
  });

  const companyTimezone = currentCompany?.timezone || 'UTC';

  const loadHolidays = useCallback(async () => {
    if (!currentCompany?.company_id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await holidayAPI.getHolidays(currentCompany.company_id);
      setHolidays(response.holidays || []);
    } catch (err: unknown) {
      console.error('Failed to load holidays:', err);
      setError(getHolidayErrorMessage(err, t('fetchFailed')));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.company_id, t]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    setNewHoliday(emptyCreateHoliday(currentCompany?.company_id || ''));
  }, [currentCompany?.company_id]);

  const filteredHolidays = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return holidays;
    return holidays.filter(holiday =>
      holiday.name.toLowerCase().includes(needle) ||
      holiday.date.includes(needle)
    );
  }, [holidays, searchTerm]);

  const formatDate = (date: string) => {
    try {
      return formatDateOnly(date);
    } catch {
      return date;
    }
  };

  const isToday = (date: string) => {
    try {
      return toDateOnly(date) === getTodayDateInCompanyTimezone(companyTimezone);
    } catch {
      const today = new Date().toISOString().split('T')[0];
      return date.startsWith(today);
    }
  };

  const handleOpenAddModal = () => {
    setError(null);
    setSuccess(null);
    setNewHoliday(emptyCreateHoliday(currentCompany?.company_id || ''));
    setShowAddModal(true);
  };

  const handleCreateHoliday = async () => {
    if (!currentCompany?.company_id || !newHoliday.name.trim() || !newHoliday.date) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.createHoliday({
        ...newHoliday,
        name: newHoliday.name.trim(),
        company_id: currentCompany.company_id,
      });
      setSuccess(t('createSuccess'));
      setShowAddModal(false);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to create holiday:', err);
      setError(getHolidayErrorMessage(err, t('saveFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setError(null);
    setSuccess(null);
    setEditingHoliday(holiday);
    setEditHoliday({
      name: holiday.name,
      date: holiday.date,
      is_recurring: Boolean(holiday.is_recurring),
    });
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday || !editHoliday.name.trim() || !editHoliday.date) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.updateHoliday(editingHoliday.id, {
        ...editHoliday,
        name: editHoliday.name.trim(),
      });
      setSuccess(t('updateSuccess'));
      setEditingHoliday(null);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to update holiday:', err);
      setError(getHolidayErrorMessage(err, t('saveFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!deletingHoliday) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.deleteHoliday(deletingHoliday.id);
      setSuccess(t('deleteSuccess'));
      setDeletingHoliday(null);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to delete holiday:', err);
      setError(getHolidayErrorMessage(err, t('deleteFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  if (companiesLoading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="d-flex justify-content-center align-items-center an-enterprise-page an-page-shell an-data-page" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted">{tc('loading.default')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canView('holidays')) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <Alert variant="danger">
            <Alert.Heading>{t('errorTitle')}</Alert.Heading>
            <p className="mb-0">{tc('errors.permissionDenied')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <div>
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          {currentCompany && canAdd('holidays') && (
            <Button variant="primary" onClick={handleOpenAddModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addHoliday')}
            </Button>
          )}
        </div>

        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}


        <ScopeIndicator resource="holidays" />

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
            {success}
          </Alert>
        )}

        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <Form onSubmit={(e) => { e.preventDefault(); }}>
                <Row>
                  <Col lg={12} md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="an-form-label">{t('searchPlaceholder')}</Form.Label>
                      <Form.Control
                        type="search"
                        placeholder={t('searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {filteredHolidays.length === 0 ? (
                <div className="text-center py-4 py-md-5">
                  <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>🎉</div>
                  <h5 className="an-empty-title">{t('noTitle')}</h5>
                  <p className="an-empty-text mb-0">
                    {searchTerm ? t('noMatch') : t('noneRegistered')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="d-none d-md-block">
                    <div className="table-responsive an-data-table an-panel">
                      <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>{t('name')}</th>
                            <th>{t('date')}</th>
                            <th>{t('isRecurring')}</th>
                            {(canEdit('holidays') || canDelete('holidays')) && <th className="text-end px-4">{tc('labels.actions')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHolidays.map((holiday) => (
                            <tr key={holiday.id}>
                              <td>
                                <div className="an-body-text">{holiday.name}</div>
                                {isToday(holiday.date) && (
                                  <Badge bg="success" className="ms-1" style={{ fontSize: '0.7rem' }}>
                                    {tc('status.today')}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <Badge bg="light" text="dark" className="fw-normal">
                                  {formatDate(holiday.date)}
                                </Badge>
                              </td>
                              <td>
                                {holiday.is_recurring ? (
                                  <Badge bg="info" className="fw-normal">{t('recurringYes')}</Badge>
                                ) : (
                                  <Badge bg="secondary" className="fw-normal">{t('recurringNo')}</Badge>
                                )}
                              </td>
                              {(canEdit('holidays') || canDelete('holidays')) && (
                                <td className="text-end px-4">
                                  <div className="d-flex justify-content-end gap-2">
                                    {canEdit('holidays') && (
                                      <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => handleEditHoliday(holiday)} title={tc('buttons.edit')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
                                    )}
                                    {canDelete('holidays') && (
                                      <Button variant="link" size="sm" className="text-danger p-1 text-decoration-none" onClick={() => setDeletingHoliday(holiday)} title={tc('buttons.delete')}><i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span></Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  <div className="d-md-none p-3">
                    {filteredHolidays.map((holiday) => (
                      <Card key={holiday.id} className="mb-3 border-0 shadow-sm an-data-card">
                        <Card.Body className="p-3 an-data-card">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="an-card-title">{holiday.name}</h6>
                              {isToday(holiday.date) && (
                                <Badge bg="success" style={{ fontSize: '0.7rem' }}>
                                  {tc('status.today')}
                                </Badge>
                              )}
                            </div>
                            {holiday.is_recurring ? (
                              <Badge bg="info" className="px-2 py-1 small">{t('recurringYes')}</Badge>
                            ) : (
                              <Badge bg="secondary" className="px-2 py-1 small">{t('recurringNo')}</Badge>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1">{t('date')}</small>
                            <Badge bg="light" text="dark" className="px-2 py-1 fw-normal">
                              {formatDate(holiday.date)}
                            </Badge>
                          </div>
                          {(canEdit('holidays') || canDelete('holidays')) && (
                            <div className="d-flex gap-2 pt-2 border-top">
                              {canEdit('holidays') && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditHoliday(holiday)}
                                  className="d-flex align-items-center"
                                >
                                  <i className="bi bi-pencil"></i>
                                  <span className="ms-1">{tc('buttons.edit')}</span>
                                </Button>
                              )}
                              {canDelete('holidays') && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => setDeletingHoliday(holiday)}
                                  className="d-flex align-items-center"
                                >
                                  <i className="bi bi-trash"></i>
                                  <span className="ms-1">{tc('buttons.delete')}</span>
                                </Button>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Form onSubmit={(e) => { e.preventDefault(); handleCreateHoliday(); }}>
            <Modal.Header closeButton className="py-2 py-md-3">
              <Modal.Title className="h5 mb-0">{t('addTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-2 py-md-3">
              <Form.Group className="mb-3">
                <Form.Label className="an-form-label">{t('nameLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  value={newHoliday.name}
                  maxLength={255}
                  placeholder={t('namePlaceholder')}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('dateLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="holiday-recurring"
                label={t('recurringLabel')}
                className="an-form-label"
                checked={newHoliday.is_recurring}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, is_recurring: e.target.checked }))}
              />
              <Form.Text className="text-muted small">{t('recurringHint')}</Form.Text>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>{tc('buttons.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={submitting || !newHoliday.name.trim() || !newHoliday.date}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.saving')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg"></i>
                    {t('addHoliday')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!editingHoliday} onHide={() => setEditingHoliday(null)} centered>
          <Form onSubmit={(e) => { e.preventDefault(); handleUpdateHoliday(); }}>
            <Modal.Header closeButton className="py-2 py-md-3">
              <Modal.Title className="h5 mb-0">{t('editTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-2 py-md-3">
              <Form.Group className="mb-3">
                <Form.Label className="an-form-label">{t('nameLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  value={editHoliday.name}
                  maxLength={255}
                  onChange={(e) => setEditHoliday(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('dateLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={editHoliday.date}
                  onChange={(e) => setEditHoliday(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="edit-holiday-recurring"
                label={t('recurringLabel')}
                className="an-form-label"
                checked={editHoliday.is_recurring}
                onChange={(e) => setEditHoliday(prev => ({ ...prev, is_recurring: e.target.checked }))}
              />
              <Form.Text className="text-muted small">{t('recurringHint')}</Form.Text>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={() => setEditingHoliday(null)} disabled={submitting}>{tc('buttons.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={submitting || !editHoliday.name.trim() || !editHoliday.date}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.saving')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-pencil"></i>
                    {tc('buttons.saveChanges')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!deletingHoliday} onHide={() => setDeletingHoliday(null)} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            {deletingHoliday && (
              <Alert variant="warning" className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('deleteConfirm', { name: deletingHoliday.name })}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2 py-md-3">
            <Button variant="secondary" onClick={() => setDeletingHoliday(null)} disabled={submitting}>{tc('buttons.cancel')}</Button>
            <Button variant="danger" onClick={handleDeleteHoliday} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('loading.deleting')}
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  {tc('buttons.delete')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Holidays;
